import React, { useState, useEffect, useMemo } from 'react';
import { Button, Modal, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import './StartEndTimePickerModal.scss';

interface StartEndTimePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (startTime: Dayjs | null, endTime: Dayjs | null) => void;
  initialStartTime: Dayjs | null;
  initialEndTime: Dayjs | null;
  timeFormat: '12h' | '24h';
  title?: string;
}

export const StartEndTimePickerModal: React.FC<StartEndTimePickerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialStartTime,
  initialEndTime,
  timeFormat,
  title,
}) => {
  const { t } = useTranslation();
  const [startTime, setStartTime] = useState<Dayjs | null>(initialStartTime);
  const [endTime, setEndTime] = useState<Dayjs | null>(initialEndTime);

  // Update internal state when initial values change
  useEffect(() => {
    setStartTime(initialStartTime);
    setEndTime(initialEndTime);
  }, [initialStartTime, initialEndTime]);

  // If end is before start, treat as overnight (roll to next day)
  const resolvedEndTime = useMemo(() => {
    if (!startTime || !endTime) return endTime;
    if (endTime.isAfter(startTime)) return endTime;
    return endTime.add(1, 'day');
  }, [startTime, endTime]);

  const isOvernight = useMemo(
    () => !!endTime && !!resolvedEndTime && resolvedEndTime.isAfter(endTime, 'day'),
    [endTime, resolvedEndTime]
  );

  const canSave = !!startTime && !!resolvedEndTime && startTime.isBefore(resolvedEndTime);

  const handleSave = () => {
    onSave(startTime, resolvedEndTime ?? endTime);
  };

  return (
    <Modal open={isOpen} onClose={onClose} className="start-end-time-picker-modal">
      <div className="start-end-time-picker-modal__content">
        <h3>{title ?? `🕒 ${t('settings.aiAssistant.setWorkHours')}`}</h3>
        <div className="start-end-time-picker-modal__time-picker">
          <div className="start-end-time-picker-modal__time-section">
            <h4>{t('settings.aiAssistant.startTime')}</h4>
            <TimePicker
              value={startTime}
              onChange={setStartTime}
              slotProps={{
                popper: {
                  className: 'custom-time-picker-popper',
                },
              }}
              format={timeFormat === '12h' ? 'h:mmA' : 'HH:mm'}
              className="start-end-time-picker-modal__time-input"
            />
          </div>
          <div className="start-end-time-picker-modal__time-section">
            <h4>{t('settings.aiAssistant.endTime')}</h4>
            <TimePicker
              value={endTime}
              onChange={setEndTime}
              slotProps={{
                popper: {
                  className: 'custom-time-picker-popper',
                },
              }}
              format={timeFormat === '12h' ? 'h:mmA' : 'HH:mm'}
              className="start-end-time-picker-modal__time-input"
            />
            {isOvernight && (
              <Typography
                variant="caption"
                className="start-end-time-picker-modal__overnight-label"
              >
                {t('settings.aiAssistant.endTimeNextDay')}
              </Typography>
            )}
          </div>
        </div>
        <div className="start-end-time-picker-modal__actions">
          <Button
            variant="text"
            onClick={onClose}
            className="start-end-time-picker-modal__cancel-button"
          >
            {t('settings.common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            variant="contained"
            className={clsx(
              'start-end-time-picker-modal__save-button',
              !canSave && 'start-end-time-picker-modal__save-button--disabled'
            )}
          >
            {t('settings.common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default StartEndTimePickerModal;
